import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ReviewAlerts } from "@/components/ReviewAlerts";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, ShoppingCart, Users, Package, FileText,
  Receipt, TrendingUp, Settings, LogOut, FileCheck, Table2, ArrowLeftRight, Flag, BellRing
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", end: true, cap: "dashboard.view" },
  { to: "/automation", icon: BellRing, label: "Automation", testid: "nav-automation", badge: "actionQueue", cap: "automation.view_runs" },
  { to: "/sales", icon: ShoppingCart, label: "Sales", testid: "nav-sales", cap: "sales.view" },
  { to: "/students", icon: Users, label: "Customers", testid: "nav-students", cap: "customers.view" },
  { to: "/catalogue", icon: Package, label: "Catalogue", testid: "nav-catalogue", cap: "catalogue.view" },
  { to: "/proformas", icon: FileText, label: "Pro-formas", testid: "nav-proformas", cap: "proformas.view" },
  { to: "/invoices", icon: FileCheck, label: "Invoices", testid: "nav-invoices", cap: "invoices.view" },
  { to: "/transactions", icon: ArrowLeftRight, label: "Transactions", testid: "nav-transactions", cap: "transactions.view" },
  { to: "/receipts", icon: Receipt, label: "Receipts", testid: "nav-receipts", cap: "receipts.view" },
  { to: "/flagged", icon: Flag, label: "Flagged", testid: "nav-flagged", cap: "flagged.view" },
  { to: "/revenue", icon: TrendingUp, label: "Revenue", testid: "nav-revenue", cap: "reports.view_revenue" },
  { to: "/reports", icon: Table2, label: "Reports", testid: "nav-reports", capAny: ["reports.view_revenue", "reports.view_commission", "reports.view_sst"] },
];

export default function AppShell() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ actionQueue: 0 });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/action-queue/count");
        if (alive) setBadges((b) => ({ ...b, actionQueue: data.total || 0 }));
      } catch { /* silent — badge just stays at last known */ }
    };
    load();
    const t = setInterval(load, 30000);
    // Refresh when the tab regains focus so operators see fresh counts immediately.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  const isVisible = (item) => {
    if (item.cap) return can(item.cap);
    if (item.capAny) return item.capAny.some((c) => can(c));
    return true;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-100 flex flex-col sidebar-noise" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-emerald-400 text-slate-900 flex items-center justify-center font-heading font-bold text-sm">
              E
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-base text-white">EliteOS</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Sales · Finance</div>
            </div>
            <div className="ml-auto"><ReviewAlerts /></div>
          </div>
        </div>
        <nav aria-label="Daily operations" className="flex-1 min-h-0 overflow-y-auto py-3" data-testid="operational-navigation">
          {NAV.filter(isVisible).map(({ to, icon: Icon, label, testid, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors " +
                (isActive
                  ? "bg-slate-800 text-white border-l-2 border-emerald-400"
                  : "text-slate-300 hover:bg-slate-800/60 hover:text-white border-l-2 border-transparent")
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate flex-1">{label}</span>
              {badge && badges[badge] > 0 && (
                <span
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-400 text-slate-900 text-[10px] font-bold tabular-nums"
                  data-testid={`nav-badge-${badge}`}
                >
                  {badges[badge] > 99 ? "99+" : badges[badge]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <nav aria-label="Administration" className="shrink-0 border-t border-slate-800 py-3" data-testid="settings-sidebar-section">
          <NavLink to="/settings" data-testid="nav-settings" className={({ isActive }) =>
            "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 " +
            (isActive ? "bg-slate-800 text-white border-emerald-400" : "text-slate-300 hover:bg-slate-800/60 hover:text-white border-transparent")
          }>
            <Settings className="w-4 h-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
            <span>Settings</span>
          </NavLink>
        </nav>
        <div className="shrink-0 p-4 border-t border-slate-800 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Signed in</div>
          <div className="text-sm text-white truncate" data-testid="current-user-email">{user?.email}</div>
          <div className="text-[11px] text-slate-400 uppercase tracking-wide">{user?.role}</div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 mt-2"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Log out
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="animate-fade-in-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
